import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Plus, Eye, EyeOff } from "lucide-react";
import { useColorSettings } from "@/contexts/ColorSettingsContext";

export function ColorManagement() {
  const [colorMappings, setColorMappings] = useState({});
  const [loading, setLoading] = useState(true);
  const [settingsId, setSettingsId] = useState(null);
  const [nextColorId, setNextColorId] = useState(16); // Empezar desde 16 para no conflictuar con existentes
  const { toast } = useToast();
  const { colorSettings, refetch } = useColorSettings();

  useEffect(() => {
    // Si tenemos colores del contexto, usarlos; sino cargar desde BD
    if (colorSettings && Object.keys(colorSettings).length > 0) {
      setColorMappings(colorSettings);
      setSettingsId(null); // No necesitamos settingsId cuando usamos contexto
      setLoading(false);
      
      // Calcular el siguiente ID de color disponible
      const existingIds = Object.keys(colorSettings).map(Number).filter(id => !isNaN(id));
      const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 15;
      setNextColorId(maxId + 1);
    } else {
      fetchColorSettings();
    }
  }, [colorSettings]);

  const fetchColorSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('id, color_mappings')
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        setSettingsId(data[0].id);
        // Convertir el formato antiguo al nuevo si es necesario
        const mappings = data[0].color_mappings || {};
        const convertedMappings = {};
        
        for (const [key, value] of Object.entries(mappings)) {
          convertedMappings[key] = typeof value === 'string' 
            ? { hex: value, description: key, active: true } 
            : { ...value, active: value.active !== false }; // Por defecto activo si no existe
        }
        
        setColorMappings(convertedMappings);
        
        // Calcular el siguiente ID de color disponible
        const existingIds = Object.keys(convertedMappings).map(Number).filter(id => !isNaN(id));
        const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 15;
        setNextColorId(maxId + 1);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los colores",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (colorId, field, value) => {
    setColorMappings(prev => ({
      ...prev,
      [colorId]: {
        ...prev[colorId],
        [field]: value
      }
    }));
  };

  const handleToggleActive = (colorId) => {
    setColorMappings(prev => ({
      ...prev,
      [colorId]: {
        ...prev[colorId],
        active: !prev[colorId].active
      }
    }));
  };

  const addNewColor = () => {
    if (Object.keys(colorMappings).length >= 50) {
      toast({
        title: "Límite alcanzado",
        description: "No se pueden agregar más de 50 colores",
        variant: "destructive"
      });
      return;
    }

    const newColorId = nextColorId.toString();
    setColorMappings(prev => ({
      ...prev,
      [newColorId]: {
        hex: '#000000',
        description: `Color ${newColorId}`,
        active: true
      }
    }));
    setNextColorId(nextColorId + 1);
  };

  const saveColorSettings = async () => {
    try {
      setLoading(true);
      
      // Siempre guardar en BD primero
      let currentSettingsId = settingsId;
      
      // Si no tenemos settingsId, obtenerlo
      if (!currentSettingsId) {
        const { data, error } = await supabase
          .from('app_settings')
          .select('id')
          .limit(1);
        
        if (error) throw error;
        if (data && data.length > 0) {
          currentSettingsId = data[0].id;
        } else {
          throw new Error('No se encontró configuración de aplicación');
        }
      }

      // Guardar cambios en BD
      const { error } = await supabase
        .from('app_settings')
        .update({ color_mappings: colorMappings })
        .eq('id', currentSettingsId);

      if (error) throw error;

      // Ahora actualizar el contexto para sincronizar otros componentes
      if (colorSettings && Object.keys(colorSettings).length > 0) {
        await refetch();
      }
      
      toast({
        title: "Configuración guardada",
        description: "Los colores se han actualizado correctamente y están sincronizados"
      });
    } catch (error) {
      console.error('Error guardando colores:', error);
      toast({
        title: "Error",
        description: "No se pudieron guardar los cambios: " + error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getActiveColorsCount = () => {
    return Object.values(colorMappings).filter(color => color.active).length;
  };

  const getTotalColorsCount = () => {
    return Object.keys(colorMappings).length;
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header con estadísticas */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Gestión de Colores</h3>
            <p className="text-sm text-gray-600">Activa/desactiva, edita y agrega colores</p>
          </div>
          <Button 
            onClick={addNewColor} 
            disabled={getTotalColorsCount() >= 50}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Agregar Color
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{getTotalColorsCount()}</div>
            <div className="text-gray-600">Total de colores</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{getActiveColorsCount()}</div>
            <div className="text-gray-600">Colores activos</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">{50 - getTotalColorsCount()}</div>
            <div className="text-gray-600">Colores disponibles</div>
          </div>
        </div>
      </div>

      {/* Lista de colores */}
      <div className="grid gap-4">
        {Object.entries(colorMappings).map(([colorId, colorData]) => (
          <div key={colorId} className={`flex items-center gap-4 p-4 rounded-lg shadow-sm border-l-4 ${
            colorData.active ? 'bg-white border-l-green-500' : 'bg-gray-50 border-l-gray-300'
          }`}>
            <div className="w-12 text-center font-medium text-gray-600">
              {colorId}
              {!colorData.active && (
                <div className="text-xs text-gray-400 mt-1">Inactivo</div>
              )}
            </div>
            <Input
              type="color"
              value={colorData.hex || '#000000'}
              onChange={(e) => handleColorChange(colorId, 'hex', e.target.value)}
              className="w-20"
            />
            <Input
              type="text"
              value={colorData.hex || '#000000'}
              onChange={(e) => handleColorChange(colorId, 'hex', e.target.value)}
              className="w-32 font-mono text-sm"
            />
            <Input
              type="text"
              placeholder="Descripción del color"
              value={colorData.description || ''}
              onChange={(e) => handleColorChange(colorId, 'description', e.target.value)}
              className="flex-1"
            />
            <div 
              className="w-8 h-8 rounded-full border"
              style={{ backgroundColor: colorData.hex || '#000000' }}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleToggleActive(colorId)}
              className={`${colorData.active ? 'text-green-600 hover:text-green-700' : 'text-gray-400 hover:text-gray-600'}`}
              title={colorData.active ? 'Desactivar color (no aparecerá en formularios)' : 'Activar color (aparecerá en formularios)'}
            >
              {colorData.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </Button>

          </div>
        ))}
      </div>

      {/* Botón guardar */}
      <Button onClick={saveColorSettings} disabled={loading} className="w-full">
        Guardar cambios
      </Button>

      {/* Información adicional */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-800 mb-2">💡 Cómo gestionar colores:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Activar/Desactivar:</strong> Usa el botón del ojo para mostrar/ocultar colores en formularios</li>
          <li>• <strong>Editar:</strong> Cambia el color hexadecimal y la descripción según necesites</li>
          <li>• <strong>Agregar:</strong> Crea nuevos colores hasta un máximo de 50</li>
          <li>• <strong>Seguridad:</strong> Los colores nunca se eliminan para preservar el historial</li>
          <li>• <strong>Colores inactivos:</strong> No aparecen en formularios pero mantienen su información</li>
        </ul>
      </div>
    </div>
  );
} 